import { useCallback, useState } from 'react';
import type { BankSelectState } from './types';
import { useBanks } from './useBanks';

interface UseBankSelectStateProps {
  initialValue?: string;
  currentValue?: string; // 외부에서 제어하는 현재 값
  onSelect?: (bank: string) => void;
}

export const useBankSelectState = ({
  initialValue,
  currentValue,
  onSelect,
}: UseBankSelectStateProps = {}) => {
  const { data: banks, error } = useBanks();
  const [internalSelectedBank, setInternalSelectedBank] = useState<string | undefined>(
    initialValue,
  );
  const [isOpen, setIsOpen] = useState(false);

  // 외부에서 currentValue가 제공되면 그것을 사용, 아니면 내부 상태 사용
  const selectedBank = currentValue !== undefined ? currentValue : internalSelectedBank;

  const select = useCallback(
    (bank: string) => {
      setInternalSelectedBank(bank);
      setIsOpen(false);
      onSelect?.(bank);
    },
    [onSelect],
  );

  const setValue = useCallback((value: string | undefined) => {
    setInternalSelectedBank(value);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const state: BankSelectState = {
    selectedBank,
    isOpen,
  };

  return {
    banks: banks?.banks || [],
    error,
    state,
    selectedBank,
    isOpen,
    select,
    setValue,
    toggle,
    close,
    open,
  };
};
